import { FilterListOutlined, Search } from "@mui/icons-material";
import {
    Box,
    Button,
    InputAdornment,
    Stack,
    TextField,
    Typography,
    ButtonProps,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Icons, theme } from "../../../assets";
import { LoadingGray } from "../../../components";
import { useFetch } from "../../../hooks";
import { useReadBudgetsStatus } from "../../../hooks/react-query/use-read-budgets-status";
import { TGetClient, TOption } from "../../../models";
import { TReadBudgetsResponse } from "../../../services/budget/read-budgets";
import readBudgetTypes, {
    TReadBudgetTypes,
    TReadBudgetTypesResponse,
} from "../../../services/budget/type/read-budget-types";
import getClients, {
    TGetClientResponse,
} from "../../../services/clients/get-clients";
import { filterPopover } from "../budgets-home";
import { BudgetTable, type DisplayedBudgetStatus } from "./budget-table";
import FilterPopover from "./filter-popover";

type TTabBudget = {
    budgets: TReadBudgetsResponse[];
    filterState: [string, React.Dispatch<React.SetStateAction<string>>];
    budgetsCountState: [number, React.Dispatch<React.SetStateAction<number>>];
    currentPageState: [number, React.Dispatch<React.SetStateAction<number>>];
    filterPopoverData: filterPopover | undefined;
    handleSetPopoverData: (data: filterPopover) => void;
    refetchBudget: () => void;
};

interface IPageButtonProps extends ButtonProps {
    name?: string;
}

export const PageButton = styled(Button, {
    shouldForwardProp: (prop) => prop !== "name",
})<IPageButtonProps>(({ theme, name }) => ({
    width: "44px",
    height: "44px",
    minWidth: 0,
    fontSize: "16px",
    borderRadius: "50%",
    padding: 0,
    lineHeight: 1,
    ...(name === "selected" && {
        backgroundColor: theme.palette.primary.light,
        color: "#ffffff",
    }),
}));

export const NoBudget = () => {
    const navigate = useNavigate();

    return (
        <Box pt={"3vh"}>
            <Stack
                width="100%"
                height="80vh"
                alignItems="center"
                flexDirection="column"
            >
                <BudgetTable
                    refetchBudget={() => {
                        console.log("null");
                    }}
                    isLoadingPage={false}
                    budgets={[]}
                />
                <Typography
                    marginTop="5vh"
                    color={theme.gray700}
                    fontWeight="normal"
                >
                    Nenhum Orçamento encontrado
                </Typography>
                <Typography color={theme.gray500}>
                    Comece um orçamento clicando no botão abaixo
                </Typography>
                <Box mt="2vh">
                    <Button
                        variant="outlined"
                        onClick={() => navigate("/financial/budget/create")}
                    >
                        <Typography
                            component="strong"
                            fontWeight="600"
                            display="flex"
                            alignItems="center"
                            fontSize="14px"
                            fontFamily="Noto Sans"
                            sx={{
                                "& svg": {
                                    marginRight: "1rem",
                                },
                            }}
                        >
                            <Icons.Plus
                                color={theme.purple500}
                                strokeWidth="3"
                            />
                            CRIAR ORÇAMENTO
                        </Typography>
                    </Button>
                </Box>
            </Stack>
        </Box>
    );
};

export const TabBudget = ({
    budgets,
    budgetsCountState: [budgetsCount, setBudgetsCount],
    filterState,
    currentPageState: [currentPage, setCurrentPage],
    filterPopoverData,
    handleSetPopoverData,
    refetchBudget,
}: TTabBudget) => {
    const [budgetTypes, setBudgetTypes] = useState<TOption[]>([]);
    const [clients, setClients] = useState<TOption[]>([]);
    const [filter, setFilter] = filterState;
    const [pagesList, setPagesList] = useState<number[]>([]);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [isLoadingPage, setIsLoadingPage] = useState(false);

    const getClientsFetch = useFetch<TGetClient, TGetClientResponse[], any>({
        fn: getClients,
        params: { active: true },
    });

    const readBudgetTypeFetch = useFetch<
        TReadBudgetTypes,
        TReadBudgetTypesResponse[],
        any
    >({
        fn: readBudgetTypes,
        params: {},
    });

    const params = {
        page: 1,
        length: 100,
    };

    const { data: busgetsStatus } = useReadBudgetsStatus({
        ...params,
    });

    const optionsStatus =
        busgetsStatus?.data?.map((status) => ({
            label: status.name,
            value: status.id_e,
            color: status.color,
        })) ?? [];

    const navigate = useNavigate();

    const handleOpen = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget);
        },
        [anchorEl, setAnchorEl]
    );

    const handleCleanFilters = () => {
        handleSetPopoverData({
            isArchived: false,
        });
    };

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, [anchorEl, setAnchorEl]);

    const isFilterPopoverEmpty = (filterPopover: Record<string, any>) =>
        Object.values(filterPopover).every((value) =>
            Array.isArray(value) ? value.length === 0 : value === false
        );

    const translateDate = (rawDate: string): string => {
        return new Date(rawDate).toLocaleDateString("pt-br");
    };

    const renderBudgetContent = () => {
        return (
            <BudgetTable
                isLoadingPage={isLoadingPage}
                budgets={budgets.map((budget) => ({
                    id: budget.id_e,
                    name: budget.name,
                    color: budget.color,
                    secondary_color: budget.secondary_color,
                    type: budget.type.name,
                    client: budget.client.name,
                    status: budget?.Status?.name ?? "Etapa de Criação",
                    created_at: translateDate(budget.created_at),
                    color_status:
                        !budget?.Status || budget?.Status?.color === "#ffffff"
                            ? "inherit"
                            : budget.Status.color,
                    hours: budget.totalHours,
                    cost: budget.totalPrice,
                    isArchived: budget.isArchived,
                }))}
                refetchBudget={refetchBudget}
            />
        );
    };

    useEffect(() => {
        const arrayLen = budgetsCount ? Math.ceil((budgetsCount ?? 1) / 10) : 1;

        setPagesList(
            Array(arrayLen)
                .fill(0)
                .map((_, i) => i + 1)
        );
    }, [budgetsCount]);

    useEffect(() => {
        if (readBudgetTypeFetch.response) {
            const responseWithOptions: TOption[] =
                readBudgetTypeFetch.response.map((bt) => ({
                    label: bt.name,
                    value: bt.id_e,
                }));

            setBudgetTypes(responseWithOptions);
        }
    }, [readBudgetTypeFetch.response]);

    useEffect(() => {
        if (getClientsFetch.response) {
            const responseWithOptions: TOption[] = getClientsFetch.response.map(
                (bt) => ({
                    label: bt.name,
                    value: bt.id_e,
                })
            );

            setClients(responseWithOptions);
        }
    }, [getClientsFetch.response]);

    useEffect(() => {
        if (isLoadingPage) {
            setIsLoadingPage(false);
        }
    }, [budgets]);

    const havePopoverFiltersApplied =
        filterPopoverData && !isFilterPopoverEmpty(filterPopoverData);

    return (
        <Box pt={"3vh"}>
            <Stack justifyContent="space-between" flexDirection="row">
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "1rem",
                    }}
                >
                    <TextField
                        label="Pesquise pelo nome do orçamento"
                        variant="standard"
                        sx={{
                            minWidth: "19rem",
                            backgroundColor: "transparent",
                        }}
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Search
                                        sx={{
                                            color: theme.gray800,
                                        }}
                                    />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="text"
                        sx={{
                            gap: "0.5rem",
                            color: havePopoverFiltersApplied
                                ? "#B93BDC"
                                : theme.primary.main,
                        }}
                        onClick={handleOpen}
                    >
                        <Box
                            sx={{
                                position: "relative",
                            }}
                        >
                            <FilterListOutlined />
                            {havePopoverFiltersApplied && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: "2px",
                                        right: "0px",
                                        width: "0.5rem",
                                        height: "0.5rem",
                                        borderRadius: "50%",
                                        backgroundColor: "#EF6C00",
                                    }}
                                />
                            )}
                        </Box>
                        FILTROS
                    </Button>
                    <FilterPopover
                        anchorEl={anchorEl}
                        budgetTypes={budgetTypes}
                        clients={clients}
                        filterPopoverData={filterPopoverData}
                        handleClose={handleClose}
                        handleSetPopoverData={handleSetPopoverData}
                        optionsStatus={optionsStatus}
                    />
                    {havePopoverFiltersApplied && (
                        <Button
                            variant="text"
                            sx={{
                                gap: "0.5rem",
                                color: "#EF6C00",
                            }}
                            onClick={handleCleanFilters}
                        >
                            LIMPAR FILTROS
                        </Button>
                    )}
                </Box>
                <Button
                    variant="contained"
                    onClick={() => navigate("/financial/budget/create")}
                >
                    <Typography
                        component="strong"
                        fontWeight="600"
                        display="flex"
                        alignItems="center"
                        fontSize="14px"
                        fontFamily="Noto Sans"
                        sx={{
                            "& svg": {
                                marginRight: "1rem",
                            },
                        }}
                    >
                        <Icons.Plus color="white" strokeWidth="3" />
                        CRIAR ORÇAMENTO
                    </Typography>
                </Button>
            </Stack>
            {renderBudgetContent()}

            <Stack
                width="100%"
                justifyContent="center"
                gap="1rem"
                mt="2rem"
                flexDirection="row"
            >
                <Button
                    sx={{
                        transform: "rotate(90deg)",
                    }}
                    onClick={() => {
                        const prevPage = currentPage - 1;
                        if (pagesList.includes(prevPage)) {
                            setIsLoadingPage(true);
                            setCurrentPage(prevPage);
                        }
                    }}
                >
                    <Icons.Chevron
                        width="3"
                        color={currentPage === 1 ? theme.gray300 : ""}
                    />
                </Button>
                {pagesList.map((page) => {
                    return (
                        <PageButton
                            key={page}
                            name={page === currentPage ? "selected" : ""}
                            onClick={() => {
                                setIsLoadingPage(true);
                                setCurrentPage(page);
                            }}
                        >
                            {page}
                        </PageButton>
                    );
                })}
                <Button
                    sx={{
                        transform: "rotate(270deg)",
                    }}
                    onClick={() => {
                        const nextPage = currentPage + 1;
                        if (pagesList.includes(nextPage)) {
                            setIsLoadingPage(true);
                            setCurrentPage(nextPage);
                        }
                    }}
                >
                    <Icons.Chevron
                        color={
                            currentPage === pagesList.length
                                ? theme.gray300
                                : ""
                        }
                        width="3"
                    />
                </Button>
            </Stack>
        </Box>
    );
};
